import { AssetManifestClient, type Theme } from '@goodtime/engine';
import giftUrl from '../../assets/icon/gift.png';

/**
 * How each slot's trail looks (every slot has one; trails have no names).
 * The token is the slot's collector symbol (engine id "cherry", its most
 * common symbol: a Gumdrop on Sherbet Square, a Starfish in the cove) and
 * the bonus boxes use its scatter symbol (Bonbon Box, Vault Door, ...).
 */
export interface TrailThemeInfo {
  /** Plural, lower case: "gumdrops". */
  collectorNoun: string;
  collectorArt: string;
  boxArt: string;
  /** The slot's reel floor, for the trail board behind it; null if none. */
  floorArt: string | null;
}

/** Art for a slot's trail. Falls back to the gift icon when the theme has
 *  no art for a symbol. */
export function trailThemeInfo(theme: Theme | null): TrailThemeInfo {
  const collector = theme?.slot?.symbols.find((s) => s.id === 'cherry');
  return {
    collectorNoun: collector?.label ? pluralise(collector.label.toLowerCase()) : 'lucky symbols',
    collectorArt: symbolUrl(theme, 'cherry') ?? giftUrl,
    boxArt: symbolUrl(theme, 'scatter') ?? giftUrl,
    floorArt: assetUrl(theme?.assets?.scene?.columnBackground),
  };
}

/** URL for one of a theme's slot symbols: the CDN copy (WebP when
 *  published) for CDN-only art, else the bundled file. */
export function symbolUrl(theme: Theme | null, symbolId: string): string | null {
  const entry = theme?.assets?.slot?.[symbolId];
  if (!entry) return null;
  if (entry.path) return AssetManifestClient.shared.resolveCdnUrl(entry.path) ?? entry.src ?? null;
  return entry.src ?? null;
}

function assetUrl(entry: { src?: string; path?: string } | undefined): string | null {
  if (!entry) return null;
  if (entry.path) return AssetManifestClient.shared.resolveCdnUrl(entry.path) ?? entry.src ?? null;
  return entry.src ?? null;
}

function pluralise(noun: string): string {
  if (/cactus$/.test(noun)) return `${noun.slice(0, -2)}i`;
  if (/[^aeiou]y$/.test(noun)) return `${noun.slice(0, -1)}ies`;
  if (/(s|x|ch|sh)$/.test(noun)) return `${noun}es`;
  return `${noun}s`;
}
